import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Grid,
  Divider, Chip,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { registerPublisherApi } from '../../api/auth';
import { loginSuccess } from '../../store/authSlice';

function FileUploadField({ label, accept, value, onChange }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        component="label"
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          border: '1px solid', borderColor: value ? 'primary.main' : 'divider',
          borderRadius: 1, px: 2, py: 1.2, cursor: 'pointer',
          bgcolor: value ? 'primary.50' : 'background.default',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          transition: 'all 0.15s',
        }}
      >
        {value
          ? <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          : <UploadFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        <Typography variant="body2" color={value ? 'primary.main' : 'text.secondary'}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {value ? value.name : 'Choose file...'}
        </Typography>
        <input type="file" accept={accept} onChange={onChange} style={{ display: 'none' }} />
      </Box>
    </Box>
  );
}

export default function PublisherRegister() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    name: '', email: '', mobile_number: '', password: '', confirm_password: '',
    company_name: '', address: '', contact_person_name: '', designation: '', website_url: '',
    pan_number: '', partnership_date: '',
  });
  const [files, setFiles] = useState({
    company_address_proof: null,
    moa_document: null,
    gst_certificate: null,
  });
  const [errors, setErrors] = useState({});
  const [nonFieldError, setNonFieldError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setFile = (k) => (e) => setFiles({ ...files, [k]: e.target.files[0] || null });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setNonFieldError('');

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
    Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });

    try {
      const res = await registerPublisherApi(fd);
      const { user, tokens } = res.data;
      if (tokens) {
        dispatch(loginSuccess({ user, tokens }));
        navigate('/publisher/dashboard');
      }
    } catch (err) {
      const data = err.response?.data || {};
      if (typeof data === 'string' || Array.isArray(data)) {
        setNonFieldError(String(data));
      } else if (data.non_field_errors) {
        setNonFieldError(data.non_field_errors[0]);
      } else {
        setErrors(data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
      <Card sx={{ maxWidth: 780, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ mt: 1 }}>Register as Publisher</Typography>
            <Typography variant="body2" color="text.secondary">
              Create your publisher account — login access is immediate
            </Typography>
          </Box>

          {nonFieldError && <Alert severity="error" sx={{ mb: 2 }}>{nonFieldError}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>

            {/* Personal Information */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700 }}>
              Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" value={form.name} onChange={set('name')}
                  required error={!!errors.name} helperText={errors.name?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Mobile Number" value={form.mobile_number}
                  onChange={set('mobile_number')} required error={!!errors.mobile_number}
                  helperText={errors.mobile_number?.[0]} inputProps={{ maxLength: 15 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Email Address" type="email" value={form.email}
                  onChange={set('email')} required error={!!errors.email}
                  helperText={errors.email?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Password" type="password" value={form.password}
                  onChange={set('password')} required error={!!errors.password}
                  helperText={errors.password?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Confirm Password" type="password"
                  value={form.confirm_password} onChange={set('confirm_password')} required
                  error={!!errors.confirm_password} helperText={errors.confirm_password?.[0]} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {/* Company Information */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700 }}>
              Company Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Company Name" value={form.company_name}
                  onChange={set('company_name')} required error={!!errors.company_name}
                  helperText={errors.company_name?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Contact Person Name" value={form.contact_person_name}
                  onChange={set('contact_person_name')} error={!!errors.contact_person_name}
                  helperText={errors.contact_person_name?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Designation" value={form.designation}
                  onChange={set('designation')} error={!!errors.designation}
                  helperText={errors.designation?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Website URL" value={form.website_url}
                  onChange={set('website_url')} error={!!errors.website_url}
                  helperText={errors.website_url?.[0]} placeholder="https://example.com" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Company Address" multiline rows={2} value={form.address}
                  onChange={set('address')} error={!!errors.address}
                  helperText={errors.address?.[0]} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {/* Company Documents */}
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                Company Documents / Details
              </Typography>
              <Chip label="At least one required" size="small" color="warning" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Provide at least one: Address Proof, MOA, PAN Number, GST Certificate, or Partnership Date.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FileUploadField
                  label="Company Address Proof (PDF / Image)"
                  accept=".pdf,.jpg,.jpeg,.png"
                  value={files.company_address_proof}
                  onChange={setFile('company_address_proof')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FileUploadField
                  label="Memorandum of Association (MOA)"
                  accept=".pdf,.doc,.docx"
                  value={files.moa_document}
                  onChange={setFile('moa_document')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="PAN Number" value={form.pan_number}
                  onChange={set('pan_number')} error={!!errors.pan_number}
                  helperText={errors.pan_number?.[0]} inputProps={{ maxLength: 20 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FileUploadField
                  label="GST Registration Certificate (PDF / Image)"
                  accept=".pdf,.jpg,.jpeg,.png"
                  value={files.gst_certificate}
                  onChange={setFile('gst_certificate')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Partnership Date" type="date" value={form.partnership_date}
                  onChange={set('partnership_date')} error={!!errors.partnership_date}
                  helperText={errors.partnership_date?.[0] || 'If applicable'}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>

            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 3 }}>
              {loading ? 'Creating Account...' : 'Create Publisher Account'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2">
              Already registered?{' '}
              <RouterLink to="/login" style={{ color: '#1565c0', fontWeight: 600 }}>Sign in</RouterLink>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
